/**
 * A short, bright "correct!" chime synthesized with the Web Audio API — no
 * audio file to ship, host, or license. Lazily creates one shared
 * AudioContext on first use; browsers require a prior user gesture before
 * audio can play, which is satisfied since this is only ever called from
 * inside the guess-submit handler.
 */

let ctx = null;

function getContext() {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!ctx) ctx = new Ctx();
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

// One arpeggio note: a sine fundamental plus a quieter triangle an octave
// up for sparkle, both with a fast attack and a short exponential decay so
// it reads as a bright "ding" rather than a drone.
function playNote(audioCtx, freq, startTime, duration, gain) {
  const osc = audioCtx.createOscillator();
  const overtone = audioCtx.createOscillator();
  const gainNode = audioCtx.createGain();
  const overtoneGain = audioCtx.createGain();

  osc.type = 'sine';
  osc.frequency.value = freq;
  overtone.type = 'triangle';
  overtone.frequency.value = freq * 2;

  gainNode.gain.setValueAtTime(0, startTime);
  gainNode.gain.linearRampToValueAtTime(gain, startTime + 0.008);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

  overtoneGain.gain.setValueAtTime(0, startTime);
  overtoneGain.gain.linearRampToValueAtTime(gain * 0.25, startTime + 0.008);
  overtoneGain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

  osc.connect(gainNode).connect(audioCtx.destination);
  overtone.connect(overtoneGain).connect(audioCtx.destination);

  osc.start(startTime);
  overtone.start(startTime);
  osc.stop(startTime + duration);
  overtone.stop(startTime + duration);
}

// C5, E5, G5, C6 — a bright major-chord arpeggio, the classic "success" shape.
const NOTES = [523.25, 659.25, 783.99, 1046.5];
const NOTE_GAP = 0.09;
const NOTE_DURATION = 0.32;

export function playCorrectSound() {
  try {
    const audioCtx = getContext();
    if (!audioCtx) return;
    const now = audioCtx.currentTime;
    NOTES.forEach((freq, i) => {
      playNote(audioCtx, freq, now + i * NOTE_GAP, NOTE_DURATION, 0.18);
    });
  } catch {
    // Audio is a nice-to-have — never let it break the guess flow.
  }
}
